import { useMemo } from 'react';

import { ActionText } from '@components/action-text';
import { Badge } from '@components/badge';
import { EmptyState } from '@components/empty-state';
import { IconButton } from '@components/icon-button';
import { IconCloudSync } from '@components/icons/cloud-sync';
import { IconCloudUnsync } from '@components/icons/cloud-unsync';
import { IconDate } from '@components/icons/date';
import { IconEdit } from '@components/icons/edit';
import { IconList } from '@components/icons/list';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPerson } from '@components/icons/person';
import { IconReplace } from '@components/icons/replace';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { FilterConfig, TableProps } from '@components/table';
import { Table } from '@components/table';
import { AvailableColumn } from '@components/table/types';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import type { List } from '../integrations-types';

export type ListTableProps = {
  lists?: List[];
  search: string;
  onEditList: (list: List) => void;
  onDeleteList: (list: List) => void;
  onChangeToManual?: (list: List) => void;
  tableStore: any;
  showSourceColumn?: boolean;
  emptyStateHeader?: string;
  emptyStateContent?: string;
};

const RenderListTag = ({ list }: { list: List }) => {
  const { t } = useScopedTranslate(locale);
  if (list.listType !== 'synced') {
    return <Badge variant="blue" size="md" text={t('manual')} />;
  }
  if (list.sync?.hasError) {
    return <Badge variant="red" size="md" icon={<IconCloudUnsync />} text={t('error')} />;
  }
  if (list.sync?.isActive) {
    return <Badge variant="green" size="md" icon={<IconCloudSync />} text={t('synced')} />;
  }
  return <Badge variant="neutral" size="md" icon={<IconCloudSync />} text={t('disabled')} />;
};

type ListTableMenuProps = {
  list: List;
  onEditList: (list: List) => void;
  onDeleteList: (list: List) => void;
  onChangeToManual?: (list: List) => void;
};

const ListTableMenu = ({
  list,
  onEditList,
  onDeleteList,
  onChangeToManual,
}: ListTableMenuProps) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Menu
      trigger={IconButton}
      triggerProps={{
        variant: 'ghost-purple',
        size: 'md',
        icon: <IconMoreVertical />,
      }}
    >
      <Menu.Item label={t('edit')} icon={<IconEdit />} onSelect={() => onEditList(list)} />
      {list.listType === 'synced' && onChangeToManual && (
        <Menu.Item
          label={t('changeToManual')}
          icon={<IconReplace />}
          onSelect={() => onChangeToManual(list)}
        />
      )}
      <Menu.Item
        label={t('delete')}
        icon={<IconTrash />}
        variant="danger"
        onSelect={() => onDeleteList(list)}
      />
    </Menu>
  );
};

export const ListTable = ({
  lists,
  search,
  onEditList,
  onDeleteList,
  onChangeToManual,
  tableStore,
  showSourceColumn = true,
  emptyStateHeader,
  emptyStateContent,
}: ListTableProps) => {
  const { t } = useScopedTranslate(locale);

  const availableColumns: Array<AvailableColumn<List>> = useMemo(() => {
    const columns: Array<AvailableColumn<List>> = [
      {
        id: 'name',
        title: t('name'),
        type: 'text',
        Cell: ({ row: list }) => (
          <Stack space={2} orientation="horizontal" align="center">
            <ActionText font="regular14" color="textPrimary" onClick={() => onEditList(list)}>
              {list.name}
            </ActionText>
          </Stack>
        ),
      },
      {
        id: 'listType',
        title: t('listType'),
        type: 'text',
        Cell: ({ row: list }) => <RenderListTag list={list} />,
      },
    ];

    if (showSourceColumn) {
      columns.push({
        id: 'source',
        title: t('source'),
        type: 'text',
        renderFallback: () => {
          return <Text text={t('noSource')} font="regular14" color="textHelper" />;
        },
      });
    }

    columns.push(
      {
        id: 'dateCreated',
        title: t('created'),
        type: 'date',
      },
      {
        id: 'numOptions',
        title: t('numOptions'),
        type: 'number',
      }
    );

    return columns;
  }, [t, onEditList, showSourceColumn]);

  const endFixedColumns: TableProps<List>['endFixedColumns'] = useMemo(
    () => [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: ({ row: list }) => (
          <ListTableMenu
            list={list}
            onEditList={onEditList}
            onDeleteList={onDeleteList}
            onChangeToManual={onChangeToManual}
          />
        ),
      },
    ],
    [onEditList, onDeleteList, onChangeToManual]
  );

  const filterConfig: FilterConfig = useMemo(() => {
    const config: FilterConfig = [
      { id: 'name', type: 'text', icon: <IconPerson />, label: t('name') },
      {
        id: 'listType',
        type: 'single_select',
        icon: <IconList />,
        label: t('listType'),
        values: [
          { value: 'manual', label: t('manual') },
          { value: 'synced', label: t('synced') },
        ],
      },
    ];

    if (showSourceColumn) {
      config.push({ id: 'source', type: 'text', icon: <IconList />, label: t('source') });
    }

    config.push(
      { id: 'dateCreated', type: 'date', icon: <IconDate />, label: t('created') },
      { id: 'numOptions', type: 'number', icon: <IconTextFormatNumber />, label: t('numOptions') }
    );

    return config;
  }, [t, showSourceColumn]);

  return (
    <Table<List>
      idColumn="shortId"
      searchStr={search}
      data={lists ?? []}
      filterProps={{ config: filterConfig, addFilterText: t('filter') }}
      availableColumns={availableColumns}
      endFixedColumns={endFixedColumns}
      store={tableStore}
      emptyState={
        <EmptyState
          icon={<IconList size={80} />}
          header={emptyStateHeader ?? t('noLists')}
          content={emptyStateContent ?? t('noListsContent')}
        />
      }
    />
  );
};

const locale = {
  en: {
    name: 'Name',
    source: 'Source',
    numOptions: '# Options',
    listType: 'List type',
    created: 'Created',
    synced: 'Synced',
    manual: 'Manual',
    disabled: 'Disabled',
    error: 'Error',
    noSource: 'No source',
    edit: 'Edit',
    delete: 'Delete',
    changeToManual: 'Change to a manual list',
    filter: 'Filter',
    noLists: 'No lists',
    noListsContent:
      "You haven't created any lists yet. Create and maintain source lists that can be accessed and used in various parts of the system",
  },
};
